import type { AuthSession, AuthUser } from '../auth/authProvider'
import { AUTH_STORAGE_PREFIX } from '../utils/storageNames'

/**
 * What the app keeps on the device to stay signed in: the long-lived session token (30 days) and the last known identity.
 * It is stored through indexedDbAuthStorage under the same key the workspace layer already watches, which is how the rest of the app learns who is
 * signed in; `user.id` therefore has to stay a top-level field of the stored JSON. The 15-minute database token is never persisted.
 */
export interface StoredNeonSession {
  v: 1
  bearer: string
  user: AuthUser
  /** When the server session ends (epoch ms), or null if unknown. */
  expiresAt: number | null
}

export function neonSessionKey(environment: string): string {
  return `${AUTH_STORAGE_PREFIX}-${environment}`
}

export function serializeSession(session: StoredNeonSession): string {
  return JSON.stringify(session)
}

export function parseStoredSession(raw: string | null): StoredNeonSession | null {
  if (!raw) return null
  let value: unknown
  try { value = JSON.parse(raw) } catch { return null }
  const record = value as Partial<StoredNeonSession> | null
  if (!record || record.v !== 1 || typeof record.bearer !== 'string' || !record.bearer) return null
  const user = record.user
  if (!user || typeof user.id !== 'string' || !user.id) return null
  return {
    v: 1,
    bearer: record.bearer,
    user: {
      id: user.id,
      name: typeof user.name === 'string' && user.name ? user.name : (typeof user.email === 'string' && user.email ? user.email : 'miniMany user'),
      email: typeof user.email === 'string' && user.email ? user.email : null,
      avatarUrl: typeof user.avatarUrl === 'string' && user.avatarUrl ? user.avatarUrl : null,
    },
    expiresAt: typeof record.expiresAt === 'number' ? record.expiresAt : null,
  }
}

export function toAuthSession(stored: StoredNeonSession): AuthSession {
  return { user: stored.user, expiresAt: stored.expiresAt }
}
